import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import { get } from '@ember/helper';
import { PhX, PhFunnel } from 'ember-phosphor-icons';
import ReSelect from 'tio-reimagined/components/select';
import Button from 'tio-reimagined/components/button';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { groupFieldOptions } from '../utils/column-utils';
import type { FieldOption } from '../utils/opensearch-field-mapper';
import {
  getFieldCategory,
  getOperatorsForCategory,
  getDefaultOperator,
  getOperatorInfo,
  parseFilterKey,
  type FilterOperator,
  type OperatorOption,
} from '../utils/filter-operators';

/**
 * A generic dataset filter item used for rendering.
 * Maps a field to its current filter value(s) and selected operator.
 */
interface FilterField extends FieldOption {
  /** Current filter value (for keyword/boolean/text fields) */
  currentValue?: unknown;
  /** Current range start (for date/numeric range fields) */
  rangeStart?: string;
  /** Current range end (for date/numeric range fields) */
  rangeEnd?: string;
  /** Currently selected operator for this field */
  currentOperator: FilterOperator;
  /** Available operators for this field's type */
  availableOperators: OperatorOption[];
}

interface DatasetFiltersSignature {
  Args: {
    /** All filterable fields for the current dataset */
    fields: FieldOption[];
    /** Current filter state (field key → value) */
    filters: Record<string, unknown>;
    /** Callback to update filters */
    onFilterUpdate: (updates: Record<string, unknown>) => void;
    /** Callback to clear all filters */
    onClearFilters: () => void;
    /** Whether fields are still loading */
    isLoading?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * DatasetFilters — Auto-generates filter controls from field metadata.
 *
 * Renders appropriate filter controls per field type:
 * - keyword fields with enumValues → dropdown (ReSelect)
 * - keyword fields without enumValues → text input
 * - date fields → date range picker (start/end)
 * - boolean fields → toggle switch
 * - integer/long/float fields → min/max number inputs
 */
export class DatasetFilters extends Component<DatasetFiltersSignature> {
  /**
   * Detect the current operator for a field by inspecting filter state keys.
   */
  private detectOperator(field: FieldOption, filters: Record<string, unknown>): FilterOperator {
    // Check for between (paired gte+lte) first, before scanning individual suffixes
    const hasGte =
      filters[`${field.key}__gte`] !== undefined && filters[`${field.key}__gte`] !== null;
    const hasLte =
      filters[`${field.key}__lte`] !== undefined && filters[`${field.key}__lte`] !== null;
    if (hasGte && hasLte) return 'between';

    // Check for explicit operator suffixes in the filter state
    const suffixedKeys = Object.keys(filters).filter(
      (k) => k.startsWith(`${field.key}__`) && filters[k] !== undefined && filters[k] !== null
    );

    for (const key of suffixedKeys) {
      const { operator } = parseFilterKey(key);
      if (operator !== 'eq') return operator;
    }

    // If base key has a value, it's an 'eq'
    if (filters[field.key] !== undefined && filters[field.key] !== null) return 'eq';

    // Default operator for the field category
    const controlType = this.getControlType(field);
    const category = getFieldCategory(controlType, field.reportDatatype, field.type);
    return getDefaultOperator(category);
  }

  /**
   * Group filterable fields by their group property.
   */
  get groupedFilterableFields(): Array<[string, FilterField[]]> {
    const filterableFields = this.args.fields.filter((f) => f.isFilterable);

    if (filterableFields.length === 0) {
      return [];
    }

    const grouped = groupFieldOptions(filterableFields);

    // Enrich with current filter values and operators
    return grouped.map(([groupName, fields]) => {
      const enriched: FilterField[] = fields.map((field) => {
        const controlType = this.getControlType(field);
        const category = getFieldCategory(controlType, field.reportDatatype, field.type);
        const currentOperator = this.detectOperator(field, this.args.filters);
        return {
          ...field,
          currentValue: this.args.filters[field.key],
          rangeStart: this.args.filters[`${field.key}__gte`] as string | undefined,
          rangeEnd: this.args.filters[`${field.key}__lte`] as string | undefined,
          currentOperator,
          availableOperators: getOperatorsForCategory(category),
        };
      });
      return [groupName, enriched] as [string, FilterField[]];
    });
  }

  get hasActiveFilters() {
    return Object.keys(this.args.filters).length > 0;
  }

  get activeFilterCount() {
    return Object.values(this.args.filters).filter((v) => v !== undefined && v !== null && v !== '')
      .length;
  }

  /**
   * Determine which control type to render for a field.
   * Checks backend ui_hints first, then falls back to type-based inference.
   */
  getControlType = (field: FieldOption) => {
    // Backend-provided UI hint takes priority
    const widget = field.uiHints?.filterWidget;
    if (widget) {
      // Map backend widget names to internal control type names
      const widgetMap: Record<string, string> = {
        select: 'select',
        multi_select: 'multi-select',
        typeahead: 'typeahead',
        boolean: 'boolean',
        date_range: 'date',
        number_range: 'number-range',
        company_select: 'select',
        text: 'text',
      };
      const mapped = widgetMap[widget];
      if (mapped) return mapped;
    }

    // Fall back to type-based inference
    const type = field.reportDatatype || field.type;

    if (type === 'boolean') return 'boolean';
    if (type === 'date' || field.isDate) return 'date';
    if (['integer', 'long', 'float', 'double', 'short'].includes(type)) {
      return field.isRangeQueryable ? 'number-range' : 'text';
    }
    if (type === 'keyword' && field.enumValues && field.enumValues.length > 0) {
      return 'select';
    }
    return 'text';
  };

  isControlType = (field: FieldOption, type: string) => {
    return this.getControlType(field) === type;
  };

  /**
   * Build select options from enumValues.
   */
  getSelectOptions = (field: FieldOption) => {
    return (field.enumValues || []).map((v) => ({ key: v, label: v }));
  };

  /**
   * Get operator select options for a filter field.
   */
  getOperatorOptions = (field: FilterField) => {
    return field.availableOperators.map((op) => ({
      key: op.key,
      label: op.label,
      description: op.description,
    }));
  };

  /**
   * Handle operator change — clear old filter keys and set new operator state.
   */
  handleOperatorChange = (fieldKey: string, newOp: string | null) => {
    if (!newOp) return;
    const op = newOp as FilterOperator;

    // Clear all existing filter entries for this field
    const clearUpdates: Record<string, unknown> = {};
    for (const key of Object.keys(this.args.filters)) {
      if (key === fieldKey || key.startsWith(`${fieldKey}__`)) {
        clearUpdates[key] = undefined;
      }
    }

    // For operators that don't require a value, set immediately
    const info = getOperatorInfo(op);
    if (!info.requiresValue && !info.requiresRange) {
      if (op === 'is_empty') {
        clearUpdates[`${fieldKey}__is_empty`] = true;
      } else if (op === 'is_not_empty') {
        clearUpdates[`${fieldKey}__is_not_empty`] = true;
      }
    } else if (op === 'eq') {
      // For eq, set the base key to empty string as placeholder
      clearUpdates[fieldKey] = '';
    } else if (info.requiresRange) {
      // For between, set both range keys as placeholders
      clearUpdates[`${fieldKey}__gte`] = '';
      clearUpdates[`${fieldKey}__lte`] = '';
    } else {
      // For other operators, set suffixed key as placeholder
      clearUpdates[`${fieldKey}__${op}`] = '';
    }

    this.args.onFilterUpdate(clearUpdates);
  };

  /**
   * Handle value change for a filter with a specific operator.
   */
  handleValueChange = (fieldKey: string, operator: FilterOperator, event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value || undefined;

    if (operator === 'eq') {
      this.args.onFilterUpdate({ [fieldKey]: value });
    } else {
      this.args.onFilterUpdate({ [`${fieldKey}__${operator}`]: value });
    }
  };

  /**
   * Handle select change for a filter with a specific operator.
   */
  handleSelectValueChange = (fieldKey: string, operator: FilterOperator, value: string | null) => {
    if (operator === 'eq') {
      this.args.onFilterUpdate({ [fieldKey]: value || undefined });
    } else {
      this.args.onFilterUpdate({ [`${fieldKey}__${operator}`]: value || undefined });
    }
  };

  /**
   * Handle multi-select change. Receives the full array of selected keys.
   */
  handleMultiSelectChange = (fieldKey: string, keys: string[]) => {
    this.args.onFilterUpdate({ [fieldKey]: keys.length > 0 ? keys : undefined });
  };

  /**
   * Handle multi-select "not in" change.
   */
  handleMultiSelectNotInChange = (fieldKey: string, keys: string[]) => {
    this.args.onFilterUpdate({ [`${fieldKey}__not_in`]: keys.length > 0 ? keys : undefined });
  };

  /**
   * Get current multi-select value as an array of strings.
   */
  getMultiSelectValue = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string' && value) return [value];
    return [];
  };

  /**
   * Handle boolean toggle.
   */
  handleBooleanChange = (fieldKey: string, event: Event) => {
    const target = event.target as HTMLSelectElement;
    const value = target.value;
    if (value === 'true') {
      this.args.onFilterUpdate({ [fieldKey]: true });
    } else if (value === 'false') {
      this.args.onFilterUpdate({ [fieldKey]: false });
    } else {
      this.args.onFilterUpdate({ [fieldKey]: undefined });
    }
  };

  /**
   * Handle date value change for a specific operator.
   */
  handleDateChange = (fieldKey: string, operator: FilterOperator, selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : undefined;

    if (operator === 'eq') {
      this.args.onFilterUpdate({ [fieldKey]: dateString });
    } else {
      this.args.onFilterUpdate({ [`${fieldKey}__${operator}`]: dateString });
    }
  };

  /**
   * Handle date range start change (for 'between' operator).
   */
  handleDateStartChange = (fieldKey: string, selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : undefined;
    this.args.onFilterUpdate({ [`${fieldKey}__gte`]: dateString });
  };

  /**
   * Handle date range end change (for 'between' operator).
   */
  handleDateEndChange = (fieldKey: string, selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : undefined;
    this.args.onFilterUpdate({ [`${fieldKey}__lte`]: dateString });
  };

  /**
   * Handle number value change for a specific operator.
   */
  handleNumberChange = (fieldKey: string, operator: FilterOperator, event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value ? Number(target.value) : undefined;

    if (operator === 'eq') {
      this.args.onFilterUpdate({ [fieldKey]: value });
    } else {
      this.args.onFilterUpdate({ [`${fieldKey}__${operator}`]: value });
    }
  };

  /**
   * Handle number range min change (for 'between' operator).
   */
  handleNumberMinChange = (fieldKey: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onFilterUpdate({
      [`${fieldKey}__gte`]: target.value ? Number(target.value) : undefined,
    });
  };

  /**
   * Handle number range max change (for 'between' operator).
   */
  handleNumberMaxChange = (fieldKey: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onFilterUpdate({
      [`${fieldKey}__lte`]: target.value ? Number(target.value) : undefined,
    });
  };

  /**
   * Clear a date range for a specific field.
   */
  clearDateStart = (fieldKey: string) => {
    this.args.onFilterUpdate({ [`${fieldKey}__gte`]: undefined });
  };

  clearDateEnd = (fieldKey: string) => {
    this.args.onFilterUpdate({ [`${fieldKey}__lte`]: undefined });
  };

  /**
   * Get the current single value for a field + operator combination.
   */
  getFilterValue = (field: FilterField): string | undefined => {
    const op = field.currentOperator;
    if (op === 'eq') {
      return this.getStringValue(field.currentValue);
    }
    const suffixedValue = this.args.filters[`${field.key}__${op}`];
    return this.getStringValue(suffixedValue);
  };

  /**
   * Get date filter value — always returns a string (empty string for no value).
   * Used for Flatpickr @date prop which expects DateOption not undefined.
   */
  getDateFilterValue = (field: FilterField): string => {
    return this.getFilterValue(field) || '';
  };

  /**
   * Helper to construct a suffixed filter key for template use.
   */
  suffixedKey = (fieldKey: string, suffix: string): string => {
    return `${fieldKey}__${suffix}`;
  };

  /**
   * Safely cast an unknown filter value to a string for template use.
   */
  getStringValue = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    return String(value);
  };

  <template>
    <div class="flex flex-col gap-y-6 text-sm" ...attributes>
      {{#if @isLoading}}
        <div class="flex items-center justify-center py-8">
          <p class="text-sm text-gray-500">Loading available filters…</p>
        </div>
      {{else if (eq this.groupedFilterableFields.length 0)}}
        <div class="flex flex-col items-center justify-center py-8 text-center">
          <PhFunnel @size={{32}} class="text-gray-400 mb-2" />
          <p class="text-sm text-gray-500">No filterable fields available for this dataset.</p>
          <p class="text-xs text-gray-400 mt-1">
            Filters will appear here when the backend provides filterable field metadata.
          </p>
        </div>
      {{else}}
        {{! Clear all filters button }}
        {{#if this.hasActiveFilters}}
          <div class="flex items-center justify-between">
            <span class="text-xs text-gray-500">
              {{this.activeFilterCount}}
              active
              {{if (eq this.activeFilterCount 1) "filter" "filters"}}
            </span>
            <Button
              @appearance="minimal"
              @intent="danger"
              @size="sm"
              {{on "click" @onClearFilters}}
            >
              Clear All Filters
            </Button>
          </div>
        {{/if}}

        {{#each this.groupedFilterableFields as |group|}}
          <fieldset>
            <legend class="sr-only">{{get group "0"}}</legend>
            <div class="flex flex-col gap-y-3">
              <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{get
                  group
                  "0"
                }}</h3>
              <div class="flex flex-col gap-y-4 pb-3 border-b border-gray-200 dark:border-gray-600">
                {{#each (get group "1") as |field|}}
                  <div class="flex flex-col gap-2">
                    {{! Field label }}
                    <label class="text-sm font-medium text-gray-900">{{field.label}}</label>

                    {{! Operator selector }}
                    <ReSelect
                      @items={{this.getOperatorOptions field}}
                      @selected={{field.currentOperator}}
                      @onChange={{fn this.handleOperatorChange field.key}}
                      @clearable={{false}}
                      @searchable={{false}}
                      @size="sm"
                    />

                    {{! Value input — varies by operator and field type }}
                    {{#if (eq field.currentOperator "is_empty")}}
                      <p class="text-xs text-gray-500 italic">Matches records where this field has
                        no value.</p>
                    {{else if (eq field.currentOperator "is_not_empty")}}
                      <p class="text-xs text-gray-500 italic">Matches records where this field has a
                        value.</p>
                    {{else if (eq field.currentOperator "between")}}
                      {{! Range input: two fields }}
                      {{#if (this.isControlType field "date")}}
                        <div class="flex items-center gap-2">
                          <div class="flex-1 relative">
                            {{! @glint-expect-error: Flatpickr type mismatch }}
                            <EmberFlatpickr
                              @date={{if field.rangeStart field.rangeStart ""}}
                              @onChange={{fn this.handleDateStartChange field.key}}
                              @dateFormat="Y-m-d"
                              @altInput={{true}}
                              @altFormat="M j, Y"
                              placeholder="Start date"
                              class="tio-form-input"
                            />
                            {{#if field.rangeStart}}
                              <button
                                type="button"
                                {{on "click" (fn this.clearDateStart field.key)}}
                                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                aria-label="Clear start date"
                              >
                                <PhX @size="14" />
                              </button>
                            {{/if}}
                          </div>
                          <span class="text-xs text-gray-500">to</span>
                          <div class="flex-1 relative">
                            {{! @glint-expect-error: Flatpickr type mismatch }}
                            <EmberFlatpickr
                              @date={{if field.rangeEnd field.rangeEnd ""}}
                              @onChange={{fn this.handleDateEndChange field.key}}
                              @dateFormat="Y-m-d"
                              @altInput={{true}}
                              @altFormat="M j, Y"
                              placeholder="End date"
                              @minDate={{if field.rangeStart field.rangeStart ""}}
                              class="tio-form-input"
                            />
                            {{#if field.rangeEnd}}
                              <button
                                type="button"
                                {{on "click" (fn this.clearDateEnd field.key)}}
                                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                aria-label="Clear end date"
                              >
                                <PhX @size="14" />
                              </button>
                            {{/if}}
                          </div>
                        </div>
                      {{else}}
                        {{! Number range }}
                        <div class="flex items-center gap-2">
                          <input
                            type="number"
                            value={{field.rangeStart}}
                            class="tio-form-input flex-1"
                            placeholder="Min"
                            aria-label="{{field.label}} minimum"
                            {{on "change" (fn this.handleNumberMinChange field.key)}}
                          />
                          <span class="text-xs text-gray-500">to</span>
                          <input
                            type="number"
                            value={{field.rangeEnd}}
                            class="tio-form-input flex-1"
                            placeholder="Max"
                            aria-label="{{field.label}} maximum"
                            {{on "change" (fn this.handleNumberMaxChange field.key)}}
                          />
                        </div>
                      {{/if}}
                    {{else if (this.isControlType field "select")}}
                      <ReSelect
                        @items={{this.getSelectOptions field}}
                        @selected={{this.getFilterValue field}}
                        @onChange={{fn
                          this.handleSelectValueChange
                          field.key
                          field.currentOperator
                        }}
                        @clearable={{true}}
                        @searchable={{true}}
                        @placeholder="Select a value…"
                        @size="sm"
                      />
                    {{else if (this.isControlType field "multi-select")}}
                      {{#if (eq field.currentOperator "not_in")}}
                        <ReSelect
                          @items={{this.getSelectOptions field}}
                          @multiple={{true}}
                          @selectedKeys={{this.getMultiSelectValue
                            (get @filters (this.suffixedKey field.key "not_in"))
                          }}
                          @onChangeKeys={{fn this.handleMultiSelectNotInChange field.key}}
                          @clearable={{true}}
                          @searchable={{true}}
                          @placeholder="Select values to exclude…"
                          @size="sm"
                        />
                      {{else}}
                        <ReSelect
                          @items={{this.getSelectOptions field}}
                          @multiple={{true}}
                          @selectedKeys={{this.getMultiSelectValue field.currentValue}}
                          @onChangeKeys={{fn this.handleMultiSelectChange field.key}}
                          @clearable={{true}}
                          @searchable={{true}}
                          @placeholder="Select one or more…"
                          @size="sm"
                        />
                      {{/if}}
                    {{else if (this.isControlType field "boolean")}}
                      <select
                        class="tio-form-input"
                        aria-label={{field.label}}
                        {{on "change" (fn this.handleBooleanChange field.key)}}
                      >
                        <option
                          value=""
                          selected={{if (eq field.currentValue undefined) true false}}
                        >Any</option>
                        <option
                          value="true"
                          selected={{if (eq field.currentValue true) true false}}
                        >Yes</option>
                        <option
                          value="false"
                          selected={{if (eq field.currentValue false) true false}}
                        >No</option>
                      </select>
                    {{else if (this.isControlType field "date")}}
                      <div class="relative">
                        {{! @glint-expect-error: Flatpickr requires many optional args }}
                        <EmberFlatpickr
                          @date={{this.getDateFilterValue field}}
                          @onChange={{fn this.handleDateChange field.key field.currentOperator}}
                          @dateFormat="Y-m-d"
                          @altInput={{true}}
                          @altFormat="M j, Y"
                          placeholder="Select a date"
                          class="tio-form-input"
                        />
                      </div>
                    {{else if (this.isControlType field "number-range")}}
                      <input
                        type="number"
                        value={{this.getFilterValue field}}
                        class="tio-form-input"
                        placeholder="Enter a number…"
                        aria-label={{field.label}}
                        {{on "change" (fn this.handleNumberChange field.key field.currentOperator)}}
                      />
                    {{else}}
                      <input
                        type="text"
                        value={{this.getFilterValue field}}
                        class="tio-form-input"
                        placeholder="Enter a value…"
                        aria-label={{field.label}}
                        {{on "change" (fn this.handleValueChange field.key field.currentOperator)}}
                      />
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            </div>
          </fieldset>
        {{/each}}
      {{/if}}
    </div>
  </template>
}

export default DatasetFilters;
